import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const HeroSection = ({ onSearchClick }) => {
  const navigate = useNavigate();
  const [particles, setParticles] = useState([]);

  useEffect(() => {
    const generateParticles = () => {
      const newParticles = Array.from({ length: 50 }, (_, i) => ({
        id: i,
        x: Math.random() * 100,
        y: Math.random() * 100,
        size: Math.random() * 4 + 2,
        duration: Math.random() * 20 + 10,
        delay: Math.random() * 5,
      }));
      setParticles(newParticles);
    };

    generateParticles();
  }, []);

  return (
    <section className="relative min-h-[350px] md:min-h-[500px] lg:min-h-[630px] flex items-center justify-center overflow-hidden bg-gradient-to-br from-[#2E8B8B] via-[#1A6B6B] to-[#0F4F4F]">
      {/* Background Image - Iconic Rock Formation */}
      <div 
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: "url('/assets/images/ChatGPT_Image_Feb_4_2026_at_07_35_55_PM-1770205072768.png')",
        }}
      />
      
      {/* Gradient Overlay - 20% opacity */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#2E8B8B]/20 via-[#1A6B6B]/20 to-[#0F4F4F]/20" />

      {/* Animated Particles */}
      <div className="absolute inset-0 overflow-hidden">
        {particles?.map((particle) => (
          <div
            key={particle?.id}
            className="absolute rounded-full bg-white/10"
            style={{
              left: `${particle?.x}%`,
              top: `${particle?.y}%`,
              width: `${particle?.size}px`,
              height: `${particle?.size}px`,
              animation: `float ${particle?.duration}s ease-in-out infinite`,
              animationDelay: `${particle?.delay}s`,
            }}
          />
        ))}
      </div>

      {/* Decorative Elements */}
      <div className="absolute top-20 left-10 w-32 h-32 bg-[#F4A261]/20 rounded-full blur-3xl" />
      <div className="absolute bottom-20 right-10 w-40 h-40 bg-white/10 rounded-full blur-3xl" />

      {/* Hero Content */}
      <div className="relative z-10 max-w-[1200px] mx-auto px-3 md:px-6 lg:px-8 text-center pt-6 md:pt-10">
        <div className="mb-2 md:mb-5 lg:mb-7">
          <div className="inline-flex items-center gap-1 px-1.5 py-0.5 backdrop-blur-sm rounded-full mb-1.5 md:mb-3 bg-[rgba(245,244,244,0.6)]">
            <Icon name="Sparkles" size={10} color="#F4A261" />
            <span className="text-[9px] md:text-xs font-semibold text-[rgba(226,111,50,1)]">
              100% VERIFIED PARTNERS
            </span>
          </div>
          
          <h1 className="text-lg md:text-3xl lg:text-4xl font-bold mb-1.5 md:mb-3 leading-tight text-teal-500">
            <span className="text-white">Discover the Hidden</span>
            <br />
            <span className="text-white">Treasures of the </span>
            <span className="bg-gradient-to-r from-[#F4A261] to-[#E89451] bg-clip-text text-orange-300">Philippines</span>
          </h1>
          
          <p className="text-[10px] md:text-base lg:text-lg text-white/90 max-w-2xl mx-auto font-medium">
            Experience authentic adventures with verified partners, AI-powered itineraries with guaranteed fair pricing in creating unforgettable memories.
          </p>
        </div>

        {/* Enhanced Search Bar */}
        <div className="max-w-3xl mx-auto mb-2 md:mb-5 lg:mb-7">
          <div className="relative w-1/2 mx-auto">
            <button
              onClick={onSearchClick}
              className="w-full flex items-center gap-1.5 md:gap-2 px-2.5 md:px-5 py-1.5 md:py-3.5 bg-white rounded-xl shadow-2xl hover:shadow-3xl transition-all duration-300 group"
            >
              <div className="w-6 h-6 md:w-8 md:h-8 rounded-lg bg-gradient-to-br from-[#2E8B8B] to-[#1A6B6B] flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                <Icon name="Search" size={12} color="#FFFFFF" />
              </div>
              <div className="flex-1 text-left">
                <span className="block text-[9px] md:text-sm font-semibold text-gray-900 mb-0.5">
                  Where do you want to explore?
                </span>
                <span className="block text-[8px] md:text-xs text-gray-500">
                  Search destinations, activities, or hidden gems...
                </span>
              </div>
              <div className="hidden md:flex items-center gap-1 px-2.5 py-1 bg-gray-100 rounded-lg">
                <span className="text-xs font-semibold text-gray-600">Ctrl</span>
                <span className="text-xs text-gray-400">+</span>
                <span className="text-xs font-semibold text-gray-600">K</span>
              </div>
            </button>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center justify-center mt-2 md:mt-4">
            <div className="w-1/2">
              <Button
                variant="default"
                size="lg"
                className="w-full bg-[#F4A261] hover:bg-[#E89451] text-gray-900 font-bold text-[9px] md:text-xs px-3 md:px-5 py-1.5 md:py-2.5 border-0 shadow-lg hover:shadow-xl"
                iconName="Sparkles"
                iconPosition="left"
                onClick={() => navigate('/trip-planner')}
              >
                AI Trip Planning
              </Button>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-1.5 md:gap-4 max-w-3xl mx-auto">
          {[
            { icon: 'Gem', value: '50+', label: 'Hidden Gems', color: '#F4A261' },
            { icon: 'BadgeCheck', value: '500+', label: 'Verified Partners', color: '#F4A261' },
            { icon: 'Users', value: '10K+', label: 'Happy Travelers', color: '#F4A261' },
          ]?.map((stat, index) => (
            <div
              key={index}
              className="p-1.5 md:p-4 bg-white/10 backdrop-blur-md rounded-xl border border-white/20 hover:bg-white/15 hover:border-white/30 transition-all duration-300 group"
            >
              <div className="w-7 h-7 md:w-12 md:h-12 rounded-lg bg-white/20 flex items-center justify-center mx-auto mb-0.5 md:mb-2 group-hover:scale-110 transition-transform duration-300">
                <Icon name={stat?.icon} size={14} color={stat?.color} />
              </div>
              <p className="text-sm md:text-2xl font-bold text-white mb-0.5">{stat?.value}</p>
              <p className="text-[8px] md:text-sm text-white/80 font-medium">{stat?.label}</p>
            </div>
          ))}
        </div>
      </div>

      <style jsx>{`
        @keyframes float {
          0%, 100% {
            transform: translateY(0) translateX(0);
          }
          25% {
            transform: translateY(-20px) translateX(10px);
          }
          50% {
            transform: translateY(-10px) translateX(-10px);
          }
          75% {
            transform: translateY(-30px) translateX(5px);
          }
        }
      `}</style>
    </section>
  );
};

export default HeroSection;